import { useMemo, useState } from 'react'
import type { BudgetCode, Source, Transaction, TransactionStatus } from '@budgetmonkey/shared'
import { Badge, EmptyState, Input, Money, Select } from '@/components/ui'
import { formatDate, formatMonth } from '@/lib/dates'

/**
 * Transaction list with the view controls workflow 7.5 asks for: grouping,
 * search-within-results, and a status filter. Shared between the transactions
 * screen and the budget code detail view so both behave identically.
 *
 * Filtering happens in memory over an already-cached fiscal year — §7.5 wants
 * this usable within three seconds of a decision, which rules out a round trip
 * per filter change.
 */

export const SOURCE_LABELS: Record<Source, string> = {
  pcard: 'P-Card',
  amazon: 'Amazon',
  po: 'PO',
  reimbursement: 'Reimb.',
  manual: 'Manual',
  csiu: 'CSIU',
}

const STATUS_TONE: Record<TransactionStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  pending: 'neutral',
  pending_refund_documentation: 'warn',
  reconciled: 'ok',
  disputed: 'warn',
  void: 'danger',
}

/**
 * "Pending" was the label for `pending`, whose opposite is "Confirmed" and
 * whose filter is "Unconfirmed" — three words for two states, and the odd one
 * out invites the question "pending what?". It was read as something to do
 * with the receipt, which is the one thing it has never meant. It means the
 * charge has not been confirmed yet: no packet submitted, no CSIU match.
 */
const STATUS_LABELS: Record<TransactionStatus, string> = {
  pending: 'Unconfirmed',
  pending_refund_documentation: 'Awaiting refund doc',
  reconciled: 'Confirmed',
  disputed: 'Disputed',
  void: 'Void',
}

export type Grouping = 'none' | 'month' | 'source'
export type StatusFilter = 'all' | 'unconfirmed' | 'confirmed'

export function TransactionList({
  transactions,
  codesById,
  showCode = true,
  onSelect,
  emptyTitle = 'No transactions yet',
  emptyBody,
  filters,
  filtersActive = false,
}: {
  transactions: readonly Transaction[]
  codesById: ReadonlyMap<string, BudgetCode>
  /** Hidden on the code detail view, where every row is the same code. */
  showCode?: boolean
  onSelect?: ((transaction: Transaction) => void) | undefined
  emptyTitle?: string
  emptyBody?: React.ReactNode
  /** Extra controls for the filter bar, rendered beside search. */
  filters?: React.ReactNode
  /** True when `transactions` has already been narrowed by those controls. */
  filtersActive?: boolean
}) {
  const [search, setSearch] = useState('')
  const [grouping, setGrouping] = useState<Grouping>('none')
  const [status, setStatus] = useState<StatusFilter>('all')

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return transactions.filter((t) => {
      if (status === 'confirmed' && t.reconciledVia === null) return false
      if (status === 'unconfirmed' && t.reconciledVia !== null) return false
      if (needle === '') return true
      const code = codesById.get(t.budgetCodeId)
      return (
        t.vendor.toLowerCase().includes(needle) ||
        t.description.toLowerCase().includes(needle) ||
        t.notes.toLowerCase().includes(needle) ||
        (code?.shortLabel.toLowerCase().includes(needle) ?? false) ||
        (code?.code.includes(needle) ?? false)
      )
    })
  }, [transactions, search, status, codesById])

  const groups = useMemo(() => {
    if (grouping === 'none') return [{ key: '', label: '', items: filtered }]
    const map = new Map<string, Transaction[]>()
    for (const t of filtered) {
      const key =
        grouping === 'month' ? formatMonth(t.transactionDate) : SOURCE_LABELS[t.source]
      const list = map.get(key)
      if (list === undefined) map.set(key, [t])
      else list.push(t)
    }
    return [...map.entries()].map(([key, items]) => ({ key, label: key, items }))
  }, [filtered, grouping])

  // Only the genuinely-empty ledger gets the empty state. Showing it for a
  // filter that matched nothing would take the filter bar away with it,
  // stranding the user with no way to undo the filter they just set.
  if (transactions.length === 0 && !filtersActive) {
    return <EmptyState title={emptyTitle}>{emptyBody}</EmptyState>
  }

  return (
    <div className="space-y-4">
      {/* A grid, not a flex row: `inputBase` sets `w-full` on every control,
          and a `w-auto` passed alongside it loses — Tailwind resolves the
          conflict by stylesheet order, not by the order of the class
          attribute. Cells size the controls instead of the controls fighting
          for it, which is why these used to stack one per line. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2 lg:col-span-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor, description, notes"
            aria-label="Search transactions"
            type="search"
          />
        </div>
        {/* Slotted so the caller's filters share this bar rather than
            stacking in a row of their own above it. */}
        {filters}
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          aria-label="Filter by confirmation status"
          className="w-auto"
        >
          <option value="all">All</option>
          <option value="unconfirmed">Unconfirmed</option>
          <option value="confirmed">Confirmed</option>
        </Select>
        <Select
          value={grouping}
          onChange={(e) => setGrouping(e.target.value as Grouping)}
          aria-label="Group transactions"
          className="w-auto"
        >
          <option value="none">No grouping</option>
          <option value="month">By month</option>
          <option value="source">By source</option>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
          Nothing matches those filters.
          {/* The count is only honest when this list is the whole year. Once
              the caller has narrowed it, `transactions` is already a subset
              and calling its length "total" would state a false number. */}
          {!filtersActive && (
            <>
              {' '}
              {transactions.length} transaction{transactions.length === 1 ? '' : 's'}{' '}
              total.
            </>
          )}
        </p>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.key} className="space-y-2">
              {group.label !== '' && (
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="text-sm font-semibold text-slate-700">{group.label}</h3>
                  <span className="text-sm text-slate-500">
                    <Money
                      value={group.items
                        .filter((t) => t.status !== 'void')
                        .reduce((sum, t) => sum + Math.round(t.amount * 100), 0) / 100}
                    />
                  </span>
                </div>
              )}
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {group.items.map((t) => (
                  <li key={t.id}>
                    <TransactionRow
                      transaction={t}
                      code={codesById.get(t.budgetCodeId)}
                      showCode={showCode}
                      onSelect={onSelect}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function TransactionRow({
  transaction,
  code,
  showCode,
  onSelect,
}: {
  transaction: Transaction
  code: BudgetCode | undefined
  showCode: boolean
  onSelect?: ((transaction: Transaction) => void) | undefined
}) {
  const content = (
    <div className="flex w-full flex-wrap items-start gap-x-4 gap-y-1.5 px-4 py-3 text-left">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{transaction.vendor}</span>
          <Badge tone={STATUS_TONE[transaction.status]}>
            {STATUS_LABELS[transaction.status]}
          </Badge>
          <Badge>{SOURCE_LABELS[transaction.source]}</Badge>
          {code?.external === true && <Badge tone="warn">External</Badge>}
          {transaction.splitGroupId !== null && <Badge>Split</Badge>}
          {/* A restaurant bill is two photos on one charge — the itemised
              slip and the signed one. Without this there is no way to see
              from a list whether the second one ever landed, which is what
              sent Matt hunting through the ledger on 2026-11-02. */}
          {transaction.receiptIds.length > 1 && (
            <Badge>{transaction.receiptIds.length} photos</Badge>
          )}
        </div>

        {transaction.description !== '' && (
          <p className="text-sm text-slate-600">{transaction.description}</p>
        )}

        <p className="text-xs text-slate-500">
          {formatDate(transaction.transactionDate)}
          {transaction.dateReceived !== transaction.transactionDate && (
            <> · received {formatDate(transaction.dateReceived)}</>
          )}
          {showCode && code !== undefined && <> · {code.shortLabel}</>}
        </p>
      </div>

      <Money
        value={transaction.amount}
        className={`shrink-0 font-medium ${
          transaction.status === 'void'
            ? 'text-slate-400 line-through'
            : transaction.amount < 0
              ? 'text-emerald-700'
              : 'text-slate-900'
        }`}
      />
    </div>
  )

  if (onSelect === undefined) return content

  return (
    <button
      type="button"
      onClick={() => onSelect(transaction)}
      className="w-full transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-slate-900"
    >
      {content}
    </button>
  )
}
